/**
 * tauriBridge.ts — Platform bridge for Tauri desktop integration.
 *
 * When the web app runs inside Tauri (desktop), this module exposes native
 * capabilities: file dialogs, stronghold vault, native notifications, system
 * tray, autostart, updater, etc.
 *
 * When running in a normal browser, every function gracefully degrades —
 * either using a browser-native equivalent or returning a sensible no-op.
 *
 * IMPORTANT: This file uses NO static or dynamic imports from @tauri-apps/*.
 * All Tauri communication goes through the __TAURI_INTERNALS__.invoke() bridge
 * so the file compiles cleanly in the normal web build with zero Tauri deps.
 */

// ─── Platform Detection ──────────────────────────────────────────────────────

type TauriInternals = {
  invoke: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
};

function getTauriInternals(): TauriInternals | null {
  return (window as Window & { __TAURI_INTERNALS__?: TauriInternals }).__TAURI_INTERNALS__ ?? null;
}

/** True when running inside Tauri desktop shell */
export function isTauri(): boolean {
  return getTauriInternals() !== null;
}

/** True when running in a normal browser tab */
export function isBrowser(): boolean {
  return !isTauri();
}

export interface PlatformInfo {
  platform: 'windows' | 'macos' | 'linux' | 'browser';
  arch: string;
  osVersion: string;
  appVersion: string;
}

export async function getPlatformInfo(): Promise<PlatformInfo> {
  const internals = getTauriInternals();
  if (internals) {
    try {
      return (await internals.invoke('get_platform_info')) as PlatformInfo;
    } catch {
      // fall through
    }
  }
  return {
    platform: 'browser',
    arch: navigator.userAgent.includes('x86_64') || navigator.userAgent.includes('Win64') ? 'x86_64' : 'unknown',
    osVersion: navigator.platform ?? 'unknown',
    appVersion: import.meta.env.VITE_APP_VERSION ?? '0.0.0',
  };
}

// ─── Tauri Invoke Helper ────────────────────────────────────────────────────

async function invoke<T = unknown>(cmd: string, args?: Record<string, unknown>): Promise<T | null> {
  const internals = getTauriInternals();
  if (!internals) return null;
  try {
    return (await internals.invoke(cmd, args)) as T;
  } catch (err) {
    console.warn(`[tauriBridge] invoke(${cmd}) failed:`, err);
    return null;
  }
}

/**
 * Call a Tauri plugin command. Plugin commands follow the pattern:
 *   plugin:<plugin_name>|<command>
 */
async function invokePlugin<T = unknown>(
  plugin: string,
  command: string,
  args?: Record<string, unknown>,
): Promise<T | null> {
  return invoke<T>(`plugin:${plugin}|${command}`, args);
}

// ─── Window Management ──────────────────────────────────────────────────────

export async function showMainWindow(): Promise<void> {
  await invoke('show_main_window');
}

export async function hideMainWindow(): Promise<void> {
  await invoke('hide_main_window');
}

// ─── Native Notifications ───────────────────────────────────────────────────

/**
 * Show a native OS notification for an incoming call.
 * Falls back to browser Notification API.
 */
export async function showCallNotification(callerName: string, callType: string): Promise<void> {
  if (isTauri()) {
    await invoke('show_call_notification', { callerName, callType });
    return;
  }
  // Browser fallback
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(`Incoming ${callType} call`, { body: `${callerName} is calling\u2026`, icon: '/eurion-icon.png' });
  }
}

/**
 * Show a native OS notification for a meeting reminder.
 * Falls back to browser Notification API.
 */
export async function showMeetingReminder(title: string, startsIn: string): Promise<void> {
  if (isTauri()) {
    await invoke('show_meeting_reminder', { title, startsIn });
    return;
  }
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('Meeting Reminder', { body: `${title} starts ${startsIn}`, icon: '/eurion-icon.png' });
  }
}

/**
 * Show a generic native notification.
 * Falls back to browser Notification API.
 */
export async function nativeNotify(title: string, body: string): Promise<void> {
  if (isTauri()) {
    await invokePlugin('notification', 'notify', { title, body });
    return;
  }
  if ('Notification' in window) {
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/eurion-icon.png' });
    }
  }
}

// ─── File Dialogs ───────────────────────────────────────────────────────────

export interface FileSelection {
  name: string;
  path: string;
  data?: Uint8Array;
}

/**
 * Open a native file picker. Falls back to an <input type="file"> in the browser.
 */
export async function pickFiles(options?: {
  multiple?: boolean;
  accept?: string[];
  title?: string;
}): Promise<FileSelection[]> {
  if (isTauri()) {
    try {
      const result = await invokePlugin<string | string[] | null>('dialog', 'open', {
        multiple: options?.multiple ?? false,
        title: options?.title ?? 'Select files',
        filters: options?.accept
          ? [{ name: 'Files', extensions: options.accept.map((e) => e.replace('.', '')) }]
          : undefined,
      });
      if (!result) return [];
      const paths = Array.isArray(result) ? result : [result];
      return paths.map((p) => ({ name: p.split(/[\\/]/).pop() ?? p, path: p }));
    } catch {
      // fall through to browser
    }
  }

  // Browser fallback: hidden file input
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = options?.multiple ?? false;
    if (options?.accept) input.accept = options.accept.join(',');
    input.onchange = () => {
      const files = Array.from(input.files ?? []);
      resolve(files.map((f) => ({ name: f.name, path: f.name })));
    };
    input.click();
  });
}

/**
 * Open a native save-file dialog. Browser fallback returns null.
 */
export async function pickSavePath(options?: {
  defaultName?: string;
  title?: string;
}): Promise<string | null> {
  if (isTauri()) {
    return invokePlugin<string>('dialog', 'save', {
      defaultPath: options?.defaultName,
      title: options?.title ?? 'Save file',
    });
  }
  return null;
}

/**
 * Reveal a file in the OS file manager. No-op in browser.
 */
export async function showInFolder(path: string): Promise<void> {
  await invoke('show_in_folder', { path });
}

// ─── Shell / URLs ───────────────────────────────────────────────────────────

/**
 * Open a URL in the default browser. In the web app, just uses window.open.
 */
export async function openUrl(url: string): Promise<void> {
  if (isTauri()) {
    const result = await invokePlugin('shell', 'open', { path: url });
    if (result !== null) return;
  }
  window.open(url, '_blank', 'noopener');
}

// ─── Autostart ──────────────────────────────────────────────────────────────

/**
 * Enable/disable autostart on login. No-op in browser.
 */
export async function setAutostart(enabled: boolean): Promise<void> {
  if (!isTauri()) return;
  if (enabled) {
    await invokePlugin('autostart', 'enable');
  } else {
    await invokePlugin('autostart', 'disable');
  }
}

/**
 * Check if autostart is enabled. Always false in browser.
 */
export async function getAutostartEnabled(): Promise<boolean> {
  if (!isTauri()) return false;
  const result = await invokePlugin<boolean>('autostart', 'is_enabled');
  return result ?? false;
}

// ─── Updater ────────────────────────────────────────────────────────────────

export async function checkForUpdates(): Promise<{ available: boolean; version?: string } | null> {
  if (!isTauri()) return null;
  return invoke('check_for_updates');
}

// ─── Stronghold Vault (Secure Token Storage) ────────────────────────────────

const VAULT_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  MLS_IDENTITY: 'mls_identity_key',
  WEBAUTHN_KEY: 'webauthn_credential_key',
  ENCRYPTION_KEY: 'e2e_encryption_key',
} as const;

/**
 * Open (or create) the Stronghold vault.
 * Falls back silently in browser (tokens stay in localStorage).
 */
export async function openVault(password: string): Promise<boolean> {
  if (!isTauri()) return false;
  try {
    // Derive key on the Rust side (Argon2id)
    const key = (await invoke<string>('derive_vault_key', { password })) ?? password;
    await invokePlugin('stronghold', 'initialize', { password: key, path: 'eurion-vault' });
    return true;
  } catch {
    return false;
  }
}

export async function storeSecret(key: string, value: string): Promise<boolean> {
  if (!isTauri()) return false;
  try {
    await invokePlugin('stronghold', 'insert', {
      store: 'eurion',
      key,
      value: Array.from(new TextEncoder().encode(value)),
    });
    await invokePlugin('stronghold', 'save');
    return true;
  } catch {
    return false;
  }
}

export async function retrieveSecret(key: string): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const data = await invokePlugin<number[]>('stronghold', 'get', {
      store: 'eurion',
      key,
    });
    if (!data) return null;
    return new TextDecoder().decode(new Uint8Array(data));
  } catch {
    return null;
  }
}

export async function deleteSecret(key: string): Promise<boolean> {
  if (!isTauri()) return false;
  try {
    await invokePlugin('stronghold', 'remove', { store: 'eurion', key });
    await invokePlugin('stronghold', 'save');
    return true;
  } catch {
    return false;
  }
}

export async function lockVault(): Promise<void> {
  // No explicit lock in Stronghold 2 plugin
}

// ─── Secure Auth Token Helpers ──────────────────────────────────────────────

/**
 * Store auth tokens. Uses Stronghold vault on desktop, localStorage in browser.
 */
export async function storeAuthTokens(accessToken: string, refreshToken: string): Promise<void> {
  if (isTauri()) {
    await storeSecret(VAULT_KEYS.ACCESS_TOKEN, accessToken);
    await storeSecret(VAULT_KEYS.REFRESH_TOKEN, refreshToken);
  }
  // Always store in localStorage too (web app reads from there)
  localStorage.setItem('eurion_token', accessToken);
  localStorage.setItem('eurion_refresh_token', refreshToken);
}

/**
 * Retrieve auth tokens. Tries Stronghold first on desktop, falls back to localStorage.
 */
export async function retrieveAuthTokens(): Promise<{ accessToken: string | null; refreshToken: string | null }> {
  if (isTauri()) {
    const accessToken = await retrieveSecret(VAULT_KEYS.ACCESS_TOKEN);
    const refreshToken = await retrieveSecret(VAULT_KEYS.REFRESH_TOKEN);
    if (accessToken) return { accessToken, refreshToken };
  }
  return {
    accessToken: localStorage.getItem('eurion_token'),
    refreshToken: localStorage.getItem('eurion_refresh_token'),
  };
}

/**
 * Clear all auth tokens from both Stronghold and localStorage.
 */
export async function clearAuthTokens(): Promise<void> {
  if (isTauri()) {
    await deleteSecret(VAULT_KEYS.ACCESS_TOKEN);
    await deleteSecret(VAULT_KEYS.REFRESH_TOKEN);
  }
  localStorage.removeItem('eurion_token');
  localStorage.removeItem('eurion_refresh_token');
}
